import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hook';
import {
  getAllCompaniesDetails,
  getAssociateCompanies,
  getLocations
} from '../redux/features/inputModule.slice';
import { DEFAULT_OPTIONS_PAYLOAD } from '../components/common/Table';
import { useGetUserCompanies } from '../backend/query';
import { FULL_USER_COMPANIES_PAYLOAD } from '../backend/license';
import { useAssociateCompanyLogo } from '../contexts/AssociateCompanyContext';
import {
  EmployeeUploadFilterState,
  EmployeeUploadSearchConfig,
  EmployeeUploadConfig,
  INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE,
  INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG,
  buildEmployeeUploadQueryPayload,
  getEmployeeUploadResetFields,
  sortEmployeeUploadCompaniesByName
} from '../utils/employeeUploads.utils';

export const useEmployeeUploadFilters = () => {
  const [filters, setFilters] = useState<EmployeeUploadFilterState>(INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE);

  const updateFilter = useCallback((field: keyof EmployeeUploadFilterState, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      ...getEmployeeUploadResetFields(field)
    }));
  }, []);

  const updateMultipleFilters = useCallback((updates: Partial<EmployeeUploadFilterState>) => {
    setFilters(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(INITIAL_EMPLOYEE_UPLOAD_FILTER_STATE);
  }, []);

  return { filters, updateFilter, updateMultipleFilters, resetFilters, setFilters };
};

export const useEmployeeUploadSearchConfig = (initialOverrides?: Partial<EmployeeUploadSearchConfig>) => {
  const [searchConfig, setSearchConfig] = useState<EmployeeUploadSearchConfig>({
    ...INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG,
    ...initialOverrides
  });

  const updateSearchConfig = useCallback((updates: Partial<EmployeeUploadSearchConfig>) => {
    setSearchConfig(prev => ({ ...prev, ...updates }));
  }, []);

  const resetSearchConfig = useCallback(() => {
    setSearchConfig(INITIAL_EMPLOYEE_UPLOAD_SEARCH_CONFIG);
  }, []);

  return { searchConfig, updateSearchConfig, resetSearchConfig };
};

export const useEmployeeUploadData = (config: EmployeeUploadConfig) => {
  const dispatch = useAppDispatch();

  const fetchData = useCallback((
    dispatchAction: any,
    filters: EmployeeUploadFilterState,
    searchConfig: EmployeeUploadSearchConfig,
    overrides: Partial<any> = {},
    sortField?: string,
    sortDirection?: 'asc' | 'desc'
  ) => {
    const payload = buildEmployeeUploadQueryPayload(filters, searchConfig, config, overrides, sortField, sortDirection);
    dispatch(dispatchAction(payload));
  }, [dispatch, config]);

  const fetchWithSearch = useCallback((
    dispatchAction: any,
    filters: EmployeeUploadFilterState,
    searchConfig: EmployeeUploadSearchConfig,
    searchInput: string
  ) => {
    const payload = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      config,
      { [config.searchFieldName]: searchInput }
    );
    dispatch(dispatchAction(payload));
  }, [dispatch, config]);

  return { fetchData, fetchWithSearch };
};

export const useEmployeeUploadCompanyData = () => {
  const dispatch = useAppDispatch();
  const companiesDetails = useAppSelector((state) => state.inputModule.companiesDetails);
  const associateCompaniesDetails = useAppSelector((state) => state.inputModule.associateCompaniesDetails);
  const locationsDetails = useAppSelector((state) => state.inputModule.locationsDetails);

  const companies = ((companiesDetails || {}).data || {}).items || [];
  const associateCompanies = ((associateCompaniesDetails || {}).data || {}).items || [];
  const locations = ((locationsDetails || {}).data || {}).items || [];

  // "idle" (not fetched yet) counts as still-loading too, so callers don't treat
  // the pre-dispatch render as "loaded, and empty".
  const companiesLoading = (companiesDetails || {}).status !== 'succeeded' && (companiesDetails || {}).status !== 'failed';
  const associateCompaniesLoading = (associateCompaniesDetails || {}).status !== 'succeeded' && (associateCompaniesDetails || {}).status !== 'failed';

  const fetchCompanies = useCallback(() => {
    const payload = { ...DEFAULT_OPTIONS_PAYLOAD, filters: { is_parent_eq: true, is_active_eq: true } };
    dispatch(getAllCompaniesDetails(payload));
  }, [dispatch]);

  const fetchAssociateCompanies = useCallback((companyId: string) => {
    const payload = {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { is_parent_eq: false, parent_company_id_eq: companyId }
    };
    dispatch(getAssociateCompanies(payload));
  }, [dispatch]);

  const fetchLocations = useCallback((associateCompanyId: string) => {
    const payload = {
      ...DEFAULT_OPTIONS_PAYLOAD,
      filters: { company_id_eq: associateCompanyId },
      include: { location: ['state', 'city'] },
      includes: { location: ['state', 'city'] },
      sorts: [`location_name asc`]
    };
    dispatch(getLocations(payload));
  }, [dispatch]);

  return {
    companies,
    associateCompanies,
    locations,
    companiesLoading,
    associateCompaniesLoading,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  };
};

export const usePerformerClientCompanyData = () => {
  const { userCompanies, isFetching } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  const companies = userCompanies || [];
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedAssocCompanyId, setSelectedAssocCompanyId] = useState<string>('');

  const selectedCompany = companies.find((c: any) => c.id === selectedCompanyId);
  const associateCompanies = (selectedCompany && selectedCompany.associate_companies) || [];

  const selectedAssocCompany = associateCompanies.find((ac: any) => ac.id === selectedAssocCompanyId);
  const locations = (selectedAssocCompany && selectedAssocCompany.locations) || [];

  const fetchCompanies = useCallback(() => {
    // No-op: data comes from useGetUserCompanies
  }, []);

  const fetchAssociateCompanies = useCallback((companyId: string) => {
    setSelectedCompanyId(companyId);
  }, []);

  const fetchLocations = useCallback((associateCompanyId: string) => {
    setSelectedAssocCompanyId(associateCompanyId);
  }, []);

  return {
    companies,
    associateCompanies,
    locations,
    // Company list comes from react-query; associate companies are derived
    // synchronously from the already-loaded company object, so there's no
    // separate loading state for them.
    companiesLoading: isFetching,
    associateCompaniesLoading: false,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations
  };
};

/**
 * Shared white-label branding behavior for the Employee Upload filters
 * (Company + Associate Company): Company keeps auto-selecting the first
 * option alphabetically once its list resolves (unchanged — Company still
 * has no "All"). Associate Company has "All" back as its resting value — it
 * is never auto-selected to a specific one, it just sits at "All" until the
 * user manually picks one. The sidenav/navbar logo is kept in sync with
 * whichever entity should currently be branding the page: the resolved
 * company's own record while Associate Company is "All" (showing the
 * PARENT company's own branding), or the specific associate company's
 * record once one is manually picked.
 *
 * Composes with either `useEmployeeUploadCompanyData` (InputModule) or
 * `usePerformerClientCompanyData` (PerformerClientAdmin) — both expose the
 * same `companies`/`companiesLoading`/`associateCompanies`/`associateCompaniesLoading`
 * shape.
 */
export const useEmployeeUploadCompanyAutoSelect = (
  filters: EmployeeUploadFilterState,
  updateFilter: (field: keyof EmployeeUploadFilterState, value: any) => void,
  companies: any[],
  companiesLoading: boolean,
  associateCompanies: any[],
  associateCompaniesLoading: boolean,
  // Pages where white-label branding doesn't apply (e.g. the Input Module's
  // Super Admin upload screens, which aren't scoped to one client) pass
  // `bindLogo: false` — the "Company auto-selects first, Associate Company
  // rests at All" filter behaviour still runs for a consistent filter UX, it
  // just never touches the shared sidenav logo. Those pages should call
  // useDefaultBrandingOnly() themselves instead.
  bindLogo: boolean = true
) => {
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();

  // Auto-select the first company alphabetically once the company list resolves.
  // Company keeps its own auto-select-first-alphabetically behavior — unchanged.
  useEffect(() => {
    if (filters.company) return; // already selected — never clobber a manual choice
    if (companiesLoading) return; // still resolving — leave the branding untouched (no flash)
    if (!companies || companies.length === 0) {
      if (bindLogo) setAssociateCompanyBranding(null); // genuinely no companies — resolve to the default
      return;
    }
    const sorted = sortEmployeeUploadCompaniesByName(companies);
    updateFilter('company', sorted[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, companiesLoading, filters.company]);

  // No auto-select for Associate Company — it rests at "All" (empty string)
  // until the user manually picks one. Branding for that resting "All" state
  // (the parent company's own branding) is applied by the effect below.

  // Keep the sidenav/navbar branding in sync with whichever entity should be
  // driving it right now: the resolved company's own record while Associate
  // Company is still "All", or the specific associate company's record once
  // one is manually picked. setAssociateCompanyBranding applies the
  // whitelabeling_enabled gate internally — callers never need to check it.
  // Never touches branding when bindLogo is false (Input Module's Super
  // Admin upload pages).
  useEffect(() => {
    if (!bindLogo) return;
    if (!filters.company) return;
    if (companiesLoading) return; // still resolving — leave the branding untouched (no flash)
    if (!filters.associateCompany) {
      // "All" shows the parent company's own branding.
      const company = (companies || []).find((c: any) => c.id === filters.company);
      setAssociateCompanyBranding(company || null);
      return;
    }
    const ac = (associateCompanies || []).find((a: any) => a.id === filters.associateCompany);
    setAssociateCompanyBranding(ac || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.company, filters.associateCompany, companies, associateCompanies, bindLogo, companiesLoading]);
};